import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { getOperatorProfile, getOperatorTours } from '../services/api';
import Navbar from '../components/Navbar';
import { 
    Box, 
    Container, 
    Grid, 
    Paper, 
    Typography, 
    Avatar,
    Divider,
    Chip,
    IconButton,
    Modal,
    Backdrop,
    Fade,
    Button
} from '@mui/material';
import {
    Phone as PhoneIcon,
    Email as EmailIcon,
    Facebook as FacebookIcon,
    Instagram as InstagramIcon,
    WhatsApp as WhatsAppIcon,
    LocationOn as LocationIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import TourCard from '../components/TourCard';
import useTranslation from '../hooks/useTranslation';

const ProfileContainer = styled(Container)`
    padding-top: 40px;
    padding-bottom: 64px;
`;

const ProfileHeader = styled(Paper)`
    padding: 40px 24px;
    margin-bottom: 40px;
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.05);
`;

const OperatorAvatar = styled(Avatar)`
    width: 200px;
    height: 200px;
    margin-bottom: 24px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    cursor: pointer;
    transition: transform 0.2s ease-in-out;
    &:hover {
        transform: scale(1.05);
    }
`;

const FullSizeImage = styled('img')`
    max-width: 90vw;
    max-height: 90vh;
    object-fit: contain;
    border-radius: 8px;
`;

const ToursGrid = styled(Grid)`
    margin-top: 24px;
`;

const ContactChip = styled(Chip)`
    margin: 8px 8px 8px 0;
    background-color: #f5f7f9;
    border: 1px solid #e0e0e0;
    &:hover {
        background-color: #eef0f2;
    }
`;

const SocialButton = styled(IconButton)`
    margin: 0 4px;
    color: #666;
    &:hover {
        color: #316C6E;
    }
`;

const OperatorProfilePage = () => {
    const { id } = useParams();
    const [operator, setOperator] = useState(null);
    const [tours, setTours] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openModal, setOpenModal] = useState(false);
    const [showFullDescription, setShowFullDescription] = useState(false);
    const { t } = useTranslation();

    const MAX_LINES = 3;

    useEffect(() => {
        const fetchOperatorData = async () => {
            try {
                const [profileData, toursData] = await Promise.all([
                    getOperatorProfile(id),
                    getOperatorTours(id)
                ]);
                setOperator(profileData);
                setTours(toursData);
            } catch (error) {
                console.error('Error fetching operator data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchOperatorData();
    }, [id]);

    const handleOpenModal = () => setOpenModal(true);
    const handleCloseModal = () => setOpenModal(false);

    const renderDescription = (text) => {
        if (!text) return null;
        
        const lines = text.split('\n').filter(line => line.trim() !== '');
        const shouldShowMore = lines.length > MAX_LINES || text.length > 150;

        return (
            <Box sx={{ textAlign: 'left' }}>
                <Typography 
                    variant="body2" 
                    sx={{ 
                        color: '#666',
                        mb: shouldShowMore ? 1 : 3,
                        lineHeight: 1.6,
                        overflow: 'hidden',
                        display: !showFullDescription ? '-webkit-box' : 'block',
                        WebkitLineClamp: !showFullDescription ? MAX_LINES : undefined,
                        WebkitBoxOrient: 'vertical'
                    }}
                >
                    {text}
                </Typography>
                {shouldShowMore && (
                    <Button 
                        onClick={() => setShowFullDescription(!showFullDescription)}
                        sx={{ 
                            color: '#316C6E',
                            p: 0,
                            minWidth: 'auto',
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            mb: 3,
                            '&:hover': {
                                background: 'none',
                                textDecoration: 'underline'
                            }
                        }}
                    >
                        {showFullDescription ? t('pages.tourModal.showLess') : t('pages.tourModal.showMore')}
                    </Button>
                )}
            </Box>
        );
    };

    if (loading) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7f9' }}>
                <Navbar hideFilters />
                <ProfileContainer>
                    <Typography>{t('common.loading')}</Typography>
                </ProfileContainer>
            </Box>
        );
    }

    if (!operator) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7f9' }}>
                <Navbar hideFilters />
                <ProfileContainer>
                    <Typography>{t('pages.tours.operator.notFound')}</Typography>
                </ProfileContainer>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7f9' }}>
            <Navbar hideFilters />
            <ProfileContainer>
                <Grid container spacing={3}>
                    {/* Operator Info Sidebar */}
                    <Grid item xs={12} md={3}>
                        <ProfileHeader elevation={0}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <OperatorAvatar 
                                    src={operator.profileImage} 
                                    alt={operator.name}
                                    onClick={handleOpenModal}
                                />
                                <Box sx={{ width: '100%' }}>
                                    <Typography 
                                        variant="h5" 
                                        sx={{ 
                                            color: '#14464F',
                                            fontWeight: 600,
                                            mb: 2,
                                            textAlign: 'center'
                                        }}
                                    >
                                        {operator.name}
                                    </Typography>
                                    
                                    {renderDescription(operator.description)}

                                    <Divider sx={{ mb: 2 }} />

                                    <Box sx={{ mb: 2 }}>
                                        {operator.phone && (
                                            <ContactChip
                                                icon={<PhoneIcon />}
                                                label={operator.phone}
                                                onClick={() => window.open(`tel:${operator.phone}`)}
                                                clickable
                                                sx={{ mb: 1, width: '100%' }}
                                            />
                                        )}
                                        {operator.email && (
                                            <ContactChip
                                                icon={<EmailIcon />}
                                                label={operator.email}
                                                onClick={() => window.open(`mailto:${operator.email}`)}
                                                clickable
                                                sx={{ mb: 1, width: '100%' }}
                                            />
                                        )}
                                        {operator.location && (
                                            <ContactChip
                                                icon={<LocationIcon />}
                                                label={operator.location}
                                                sx={{ mb: 1, width: '100%' }}
                                            />
                                        )}
                                    </Box>

                                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                                        {operator.facebook && (
                                            <SocialButton onClick={() => window.open(operator.facebook)}>
                                                <FacebookIcon />
                                            </SocialButton>
                                        )}
                                        {operator.instagram && (
                                            <SocialButton onClick={() => window.open(operator.instagram)}>
                                                <InstagramIcon />
                                            </SocialButton>
                                        )}
                                        {operator.whatsapp && (
                                            <SocialButton onClick={() => window.open(`https://wa.me/${operator.whatsapp}`)}>
                                                <WhatsAppIcon />
                                            </SocialButton>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        </ProfileHeader>
                    </Grid>

                    {/* Tours Section */}
                    <Grid item xs={12} md={9}>
                        <Box sx={{ mb: 3 }}>
                            <Typography 
                                variant="h5" 
                                sx={{ 
                                    color: '#14464F',
                                    fontWeight: 600,
                                    mb: 3
                                }}
                            >
                                {t('pages.tours.operator.availableTours')}
                            </Typography>

                            {tours.length === 0 ? (
                                <Paper 
                                    elevation={0} 
                                    sx={{ 
                                        p: 3, 
                                        textAlign: 'center',
                                        bgcolor: 'white',
                                        borderRadius: 2
                                    }}
                                >
                                    <Typography color="text.secondary">
                                        {t('pages.tours.operator.noTours')}
                                    </Typography>
                                </Paper>
                            ) : (
                                <ToursGrid container spacing={3}>
                                    {tours.map((tour) => (
                                        <Grid item xs={12} sm={6} md={4} key={tour.id}>
                                            <TourCard tour={tour} />
                                        </Grid>
                                    ))}
                                </ToursGrid>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </ProfileContainer>

            {/* Full-size Image Modal */}
            <Modal
                open={openModal}
                onClose={handleCloseModal}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2
                }}
            >
                <Fade in={openModal}>
                    <Box sx={{ position: 'relative' }}>
                        <IconButton
                            onClick={handleCloseModal}
                            sx={{
                                position: 'absolute',
                                top: -40,
                                right: -40,
                                color: 'white',
                                bgcolor: 'rgba(0,0,0,0.4)',
                                '&:hover': {
                                    bgcolor: 'rgba(0,0,0,0.6)'
                                }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                        <FullSizeImage
                            src={operator?.profileImage}
                            alt={operator?.name}
                        />
                    </Box>
                </Fade>
            </Modal>
        </Box>
    );
};

export default OperatorProfilePage; 